/**
 * Admission control client.
 *
 * Surfaces the admission "inbox" (open proposals), accept/decline, and the
 * per-user autonomy mode. Every actionable item created by the user or by
 * yanamas chat is admitted through a durable pipeline (pending_admission ->
 * evaluate -> auto-place / park / propose). When the orchestrator can't act
 * on its own (a tradeoff that pauses something the user owns, missing detail,
 * an offer to break a big item into steps), it raises a proposal here.
 *
 * Every call is non-blocking: a 503 (feature flag off) or any error returns a
 * safe empty/false value so wiring this in never breaks a screen.
 *
 * Backend: /api/admission/v1 (see src/domains/admission/routes).
 */

export type AdmissionAutonomyMode =
  | 'suggest_only'
  | 'safe_auto_adjust'
  | 'proactive';

export type AdmissionProposalKind =
  | 'tradeoff'
  | 'needs_information'
  | 'decompose'
  | 'pursue_now'
  | 'batch_release';

export interface AdmissionProposal {
  id: number;
  decision_id: number | null;
  item_id: number;
  item_type: string;
  kind: AdmissionProposalKind;
  title: string | null;
  /** Tone-guarded copy; render verbatim. */
  copy: string;
  options: Record<string, unknown>;
  requires_confirmation: boolean;
  based_on_item_version: number;
  created_at: string | null;
  expires_at: string | null;
}

const BASE = '/api/admission/v1';

async function postJson(path: string): Promise<Record<string, unknown> | null> {
  try {
    const resp = await fetch(`${BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!resp.ok) {
      if (resp.status !== 503) console.warn('[admission]', path, resp.status);
      return null;
    }
    return (await resp.json()) as Record<string, unknown>;
  } catch (e) {
    console.warn('[admission] request failed (non-blocking):', e);
    return null;
  }
}

/** Fetch the user's open proposals (the admission inbox). */
export async function listProposals(): Promise<AdmissionProposal[]> {
  try {
    const resp = await fetch(`${BASE}/proposals`, { credentials: 'include' });
    if (!resp.ok) return [];
    const body = (await resp.json()) as { proposals?: AdmissionProposal[] };
    return body.proposals ?? [];
  } catch (e) {
    console.warn('[admission] listProposals failed:', e);
    return [];
  }
}

/** Accept a proposal (place / displace / decompose / supply info). */
export async function acceptProposal(proposalId: number): Promise<boolean> {
  const body = await postJson(`/proposals/${proposalId}/accept`);
  return Boolean(body?.['ok']);
}

/** Decline a proposal — the item is parked (saved, not lost), not deleted. */
export async function declineProposal(proposalId: number): Promise<boolean> {
  const body = await postJson(`/proposals/${proposalId}/decline`);
  return Boolean(body?.['ok']);
}

/** Get the current autonomy mode (how much yanamas acts on its own). */
export async function getAutonomyMode(): Promise<AdmissionAutonomyMode | null> {
  try {
    const resp = await fetch(`${BASE}/autonomy`, { credentials: 'include' });
    if (!resp.ok) return null;
    const body = (await resp.json()) as { mode?: AdmissionAutonomyMode };
    return body.mode ?? null;
  } catch {
    return null;
  }
}

/** Set the autonomy mode. Returns the saved mode, or null on failure. */
export async function setAutonomyMode(
  mode: AdmissionAutonomyMode,
): Promise<AdmissionAutonomyMode | null> {
  try {
    const resp = await fetch(`${BASE}/autonomy`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode }),
    });
    if (!resp.ok) return null;
    const body = (await resp.json()) as { mode?: AdmissionAutonomyMode };
    return body.mode ?? null;
  } catch {
    return null;
  }
}
